import React, { useEffect } from 'react'
import { Alert, Button, Flex, Form, Input, InputNumber, Select, Radio } from 'antd'
import Access from '../../../components/access/Access'
import Text from 'antd/es/typography/Text'
import { TextContentRules } from '../../../components/access/access-rules/assignment-rules'
import { QUESTION_NAME_LIMIT } from '../../../../../constants/input-limits'
import { State } from '../../../../../constants/assignment-permissions'
import { renderOptionWithDescription } from '../../../../../utils/antd.helper'

const { TextArea } = Input

export const QuestionTypes = {
    STANDARD: 'standard',
    CONTEXT_ONLY: 'context_only',
    GLOBAL: 'global',
    INPUT_ONLY: 'input_only',
}

export const questionTypeOptions = [
    { 
        label: 'Standard', 
        value: QuestionTypes.STANDARD,
        description: 'A regular question that the student is expected to answer. The answer will be evaluated and graded by the system.'
    },
    { 
        label: 'Global', 
        value: QuestionTypes.GLOBAL,
        description: "The student's answer will be evaluated in the context of all students' answers."
    },
    { 
        label: 'Context Only', 
        value: QuestionTypes.CONTEXT_ONLY,
        description: 'No answer is expected from the student. This question provides contextual information for other questions.'
    },
    { 
        label: 'Input Only', 
        value: QuestionTypes.INPUT_ONLY,
        description: "The student is expected to provide an answer, but it will not be graded. The answer is used as an input or dependency for other questions (for example, collecting the student's country)."
    },
]

export const getQuestionTypeLabel = questionType => {
    return questionTypeOptions.find(o => o.value === questionType)?.label || 'Standard'
}

const QuestionForm = ({
    onSubmit,
    initialValues,
    error,
    loading,
    earlierQuestions,
    formResetTrigger,
    questions,
    form: externalForm,
    submitButtonText = 'Submit',
}) => {
    const [form] = Form.useForm(externalForm)

    const dependsOnOptions =
        earlierQuestions?.map(question => ({
            label: question.name || question.content,
            value: question.id,
        })) || []

    useEffect(() => {
        form.resetFields()
    }, [form, formResetTrigger])

    const isValueUnique = (value, field, currentQuestionId) => {
        if (!value || !questions) return true
        return !questions.some(
            question => question[field]?.toLowerCase() === value.toLowerCase() && question.id !== currentQuestionId,
        )
    }

    const validateQuestionField = (field, value, currentQuestionId) => {
        if (!isValueUnique(value, field, currentQuestionId))
            return Promise.reject(`Question ${field} must be unique among all questions`)
        return Promise.resolve()
    }

    const handleSubmit = async values => {
        const result = await onSubmit(values)

        // If submission was successful, reset touched state but keep values
        if (result !== false) form.setFields(Object.keys(form.getFieldsValue()).map(name => ({ name, touched: false })))

        return result
    }

    return (
        <Form
            form={form}
            name='question-form'
            autoComplete='off'
            onFinish={handleSubmit}
            requiredMark={false}
            layout='vertical'
        >
            <Form.Item
                name='name'
                label='Name'
                initialValue={initialValues?.name}
                rules={[
                    { validator: (rule, value) => validateQuestionField('name', value, initialValues?.id) },
                    { max: QUESTION_NAME_LIMIT, message: `Question name cannot exceed ${QUESTION_NAME_LIMIT} characters` }
                ]}
            >
                <Access
                    rules={TextContentRules.EDIT_NAMINGS}
                    fallback={<Text>{initialValues?.name || 'No name provided'}</Text>}
                >
                    <Input 
                        placeholder='Enter question name (otherwise the content will be used as a name)' 
                        maxLength={QUESTION_NAME_LIMIT}
                        showCount
                        data-test="question-name-input"
                    />
                </Access>
            </Form.Item>
            <Form.Item
                name='text'
                label='Question Text'
                initialValue={initialValues?.text}
                tooltip='The full text of the question that students will see and respond to'
            >
                <Access
                    rules={TextContentRules.EDIT_NAMINGS}
                    fallback={
                        <Text style={{ whiteSpace: 'pre-wrap' }}>
                            {initialValues?.text || 'No question text provided'}
                        </Text>
                    }
                >
                    <TextArea
                        placeholder='Enter the full question text that students will see'
                        autoSize={{ minRows: 3, maxRows: 10 }}
                        data-test="question-text-input"
                    />
                </Access>
            </Form.Item>
            <Form.Item name='questionType' label='Question Type' initialValue={initialValues?.questionType || QuestionTypes.STANDARD}>
                <Access
                    rules={[...TextContentRules.EDIT_GRADINGS, State.NO_SUBMISSIONS]}
                    fallback={<Text>{questionTypeOptions.find(o => o.value === initialValues?.questionType)?.label || 'Standard'}</Text>}
                    disableWhenNoAccess
                >
                    <Select
                        options={questionTypeOptions}
                        popupMatchSelectWidth={false}
                        dropdownStyle={{ maxWidth: 320 }}
                        data-test="question-type-input"
                        optionRender={renderOptionWithDescription}
                    />
                </Access>
            </Form.Item>
            <Flex justify='space-between'>
                <Form.Item
                    name='wordsLimit'
                    label='Word Limit'
                    rules={[{ type: 'number', min: 0 }]}
                    initialValue={initialValues?.wordsLimit}
                >
                    <Access
                        rules={TextContentRules.EDIT_GRADINGS}
                        fallback={<Text>{initialValues?.wordsLimit ?? 'No word limit set'}</Text>}
                    >
                        <InputNumber addonAfter='words' style={{ width: 144 }} step={10} data-test="question-word-limit-input" />
                    </Access>
                </Form.Item>
            </Flex>

            {Boolean(dependsOnOptions.length) && (
                <Form.Item
                    name='dependsOnQuestionIds'
                    label='Depends On'
                    initialValue={initialValues?.dependsOnQuestionIds}
                >
                    <Access
                        rules={TextContentRules.EDIT_GRADINGS}
                        fallback={
                            <Text>
                                {initialValues?.dependsOnQuestionIds?.length
                                    ? dependsOnOptions
                                          .filter(option => initialValues.dependsOnQuestionIds.includes(option.value))
                                          .map(option => option.label)
                                          .join(', ')
                                    : 'This question does not depend on any other questions'}
                            </Text>
                        }
                    >
                        <Select
                            mode='multiple'
                            allowClear
                            style={{ width: '100%' }}
                            placeholder='Select the earlier questions this question depends on if any'
                            options={dependsOnOptions}
                            maxCount={1}
                            showSearch
                            optionFilterProp='label'
                            popupMatchSelectWidth={false}
                            getPopupContainer={triggerNode => triggerNode.parentNode}
                            data-test="question-depends-on-input"
                        />
                    </Access>
                </Form.Item>
            )}
            <Form.Item
                name='note'
                label='Note'
                initialValue={initialValues?.note}
            >
                <Access
                    rules={TextContentRules.EDIT_NAMINGS}
                    fallback={<Text>{initialValues?.note || 'No note provided'}</Text>}
                >
                    <TextArea
                        placeholder='Enter an internal note for this question (optional)'
                        autoSize={{ minRows: 2, maxRows: 6 }}
                        data-test="question-note-input"
                    />
                </Access>
            </Form.Item>
            {error && <Alert message={error} type='error' showIcon closable />}
            <Form.Item>
                <Access rules={TextContentRules.EDIT_NAMINGS}>
                    <Button type='primary' htmlType='submit' block loading={loading} data-test="question-submit-button">
                        {submitButtonText || 'Save'}
                    </Button>
                </Access>
            </Form.Item>
        </Form>
    )
}

export default QuestionForm
